/**
 * <ticket-template-preview> — one variant's card at a caller-chosen width,
 * rendered against the operator's own brand (`ticketPreviewProps`). The one
 * tag every preview surface mounts: the gallery's tiles
 * (ticket-template-catalog.view.ts, 280px), the form control's thumbnails
 * (ticket-template-picker.view.ts, 120px), and the studio's wallet proof
 * (features/wallet-preview/wallet-preview-module.view.ts, 240px).
 *
 * WHY A TAG AND NOT A SHARED RENDER CALL. Resolving a card's props is state
 * work, and `@momo/ticket-template` is state-free by contract
 * (ticket.props.ts): props arrive already resolved, and a variant file never
 * reaches for a deployment value. So that package cannot hold the resolution,
 * and each preview surface holding its own would be three hand-written copies
 * of one card. This element is where that resolution lives — exactly as
 * `<ticket-card>` is where a real buyer's ticket's resolution lives — and the
 * DESIGN stays one package over, untouched by either.
 *
 * The width reaches the scale frame, never the composition: the card lays out
 * at its native 384px and the whole frame is transform-scaled
 * (`renderTicketCardScaled`), so 120, 240 and 280 are the same object at three
 * sizes rather than three re-flows.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { renderTicketCardScaled } from "@momo/ticket-template";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
} from "@momo/types";
import { css, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { ticketPreviewProps } from "./preview.state.ts";

@schmancyElement("ticket-template-preview")
export class TicketTemplatePreview extends SchmancyElement {
	// The scale frame emits a fixed-pixel box, so the host hugs it rather than
	// stretching to whatever column it was dropped into — `<ticket-card>`'s own
	// rule, for the same frame.
	static styles = [
		css`
			:host {
				display: block;
				width: max-content;
			}
		`,
	];

	@property({ attribute: false })
	variant: TicketTemplateVariant = DEFAULT_TICKET_TEMPLATE_VARIANT;

	/** The card's on-screen width in px — a number, not a fluid CSS width: a
	 *  ticket scales, never re-flows. */
	@property({ type: Number }) width = 384;

	override render(): TemplateResult {
		return renderTicketCardScaled(this.variant, ticketPreviewProps.get(), {
			width: this.width,
		});
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-template-preview": TicketTemplatePreview;
	}
}
